// ============================================================================
// EFFECTIVE TIMES HELPER
// Computes effective check-in/check-out times from task overrides or reservation
// ============================================================================

export interface EffectiveTimes {
  checkIn: string | null;
  checkOut: string | null;
}

export interface TaskWithOverrides {
  override_check_in_time?: string | null;
  override_check_out_time?: string | null;
}

export interface ReservationWithTimes {
  check_in_time?: string | null;
  check_out_time?: string | null;
}

/**
 * Computes effective check-in and check-out times.
 * Priority: task override → reservation time → null
 */
export function computeEffectiveTimes(
  task: TaskWithOverrides | null | undefined,
  reservation: ReservationWithTimes | null | undefined
): EffectiveTimes {
  return {
    checkIn: task?.override_check_in_time ?? reservation?.check_in_time ?? null,
    checkOut: task?.override_check_out_time ?? reservation?.check_out_time ?? null,
  };
}

/**
 * Parses a time string (e.g., "11:00 AM", "14:00") and returns a Date object for today.
 * Returns null if parsing fails.
 */
export function parseTimeToDate(timeStr: string | null | undefined): Date | null {
  if (!timeStr) return null;
  
  const now = new Date();
  const normalized = timeStr.trim().toUpperCase();
  
  // Handle "Flexible", "Unknown", etc.
  if (normalized === "FLEXIBLE" || normalized === "UNKNOWN" || normalized === "N/A") {
    return null;
  }
  
  // Try parsing 12-hour format (e.g., "11:00 AM", "3:30 PM")
  const match12 = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = parseInt(match12[2], 10);
    const isPM = match12[3].toUpperCase() === "PM";
    
    if (isPM && hours !== 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
    
    const result = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
    return result;
  }
  
  // Try parsing 24-hour format (e.g., "14:00", "09:30")
  const match24 = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1], 10);
    const minutes = parseInt(match24[2], 10);
    
    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      const result = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
      return result;
    }
  }
  
  return null;
}

/**
 * Checks if the checkout time has passed.
 */
export function hasCheckoutTimePassed(checkoutTime: string | null | undefined): boolean | null {
  const checkoutDate = parseTimeToDate(checkoutTime);
  if (!checkoutDate) return null; // Unknown
  
  return new Date() >= checkoutDate;
}
